import { Box, Slide } from "@mui/material";
import queryString from "query-string";
import React, { Dispatch, DispatchWithoutAction, useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

/** A component which identifies when its login step requirements are met. */
export interface ILoginStepProps {
  /** Callback to identify when the login step is completed. */
  onComplete: DispatchWithoutAction;
}

export interface ILoginProcessStepConfiguration {
  /** The component to be rendered for the step. */
  Component: React.FC<ILoginStepProps>;
  /** Whether or not an animation should be shown when the step becomes active. */
  showAnimation: boolean;
}

export interface ILoginProcessProps {
  /** The window's location search parameters.
   * @remarks this is passed in rather than accessed via `window.location` for testing purposes.
   */
  windowSearchParams: string;
  /** The step which should be shown to the user and will report when they have been resolved. */
  steps: ILoginProcessStepConfiguration[];
  /** The callback to be executed when all login steps have been completed. */
  onComplete: DispatchWithoutAction;
  /** Callback to establish the id of the lead that should be started immediately upon login. */
  setLoginLeadId: Dispatch<string>;
}

interface ILoginProcessStep extends Pick<ILoginProcessStepConfiguration, "Component"> {
  isActive: boolean;
  onComplete: DispatchWithoutAction;
}

const SlidingStep: React.FC<ILoginProcessStep> = ({ Component, isActive, onComplete }) => (
  <Box position="absolute" display={isActive ? undefined : "none"}>
    <Slide in={isActive} direction="left" timeout={500} appear={false}>
      <div>
        <Component onComplete={onComplete} />
      </div>
    </Slide>
  </Box>
);

/** The component responsible for showing the user a series of login steps and navigating to root when they have
 * all reported that they are completed.
 */
const LoginProcess: React.FC<ILoginProcessProps> = ({ steps, windowSearchParams, onComplete, setLoginLeadId }) => {
  const [loginStepProgress, setLoginStepProgress] = useState(steps.map(() => false));
  useEffect(() => {
    const { leadId, loginExpired } = queryString.parse(windowSearchParams);
    if (leadId) {
      setLoginLeadId(leadId as string);
      // if pushed here by token expiring and have a lead id, toast
      if (loginExpired) toast.info("Session timed out. Please log in to continue.");
    }
  }, []);

  useEffect(() => {
    if (loginStepProgress.every(isFinished => isFinished)) onComplete();
  }, [loginStepProgress, onComplete]);

  const getOnCompleteForIndex = useCallback(
    (index: number) => () =>
      setLoginStepProgress(p => {
        if (p[index] == true) return p;
        p[index] = true;
        return [...p];
      }),
    []
  );

  const firstIncompleteStepIndex = loginStepProgress.findIndex(p => !p);

  return (
    <Box height="100%" display="flex" justifyContent="center" alignItems="center">
      {steps.map(({ Component, showAnimation }, index) => {
        const isActive = index === firstIncompleteStepIndex;
        return showAnimation ? (
          <SlidingStep
            isActive={isActive}
            Component={Component}
            onComplete={getOnCompleteForIndex(index)}
            key={index}
          />
        ) : (
          <Box position="absolute" display={isActive ? undefined : "none"} key={index}>
            <Component onComplete={getOnCompleteForIndex(index)} />
          </Box>
        );
      })}
    </Box>
  );
};

export default LoginProcess;
